import "core-js/modules/es.array.push.js";
import { useAppApiClient } from 'app/api/app-api/AppApiClientContext';
import { useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import PageHeader from "../../core/App/PageHeader";
import PagePanel from "../../core/App/PagePanel";
import NotFound from "../../core/NotFound/NotFound";
import { LoadingSpinner } from "../../core/components/LoadingSpinner";
import ContractStatusDot from "../../core/components/StatusDot/ContractStatusDot";
import { constantToDisplayName } from "../../core/constantToDisplayName";
import { useFormatUserDate } from "../../core/useFormatUserDate";
import { RequireAuth, UserRole, useMeQuery } from 'legoland-sdk/dist/experimental';
import { useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Alert, Box, Button, Heading, Label, Modal, Text, Tooltip, propStyling, tombac, useToasts } from 'tombac';
import { BackIcon, CopyIcon, EditIcon, ExportIcon } from 'tombac-icons';
import { ContractAccessTypeIcon } from "../common/ContractAccessTypeIcon";
import { ContractExpires } from "../common/ContractExpires";
import { ContractProductsContainer } from "../common/ContractProductsContainer";
import { ContractTerm } from "../common/ContractTerm";
import { ContractUserList } from "../common/ContractUserList";
import { getContractDisplayName } from "../contractHelpers";
import { useContractQuery } from "../contractsApiHooks";
import { AssignContractUsers } from "../form/AssignContractUsers";
import { ContractFormModal } from "../form/ContractFormModal";
import { ContractAssignOverlapsModal } from "./ContractAssignOverlapsModal";
import "./ContractDetails.css";
import { ContractReportModal } from "./report/ContractReportModal";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ContractFieldText = styled(Text).withConfig({
  displayName: "ContractDetails__ContractFieldText"
})(["color:", ";font-size:", ";"], tombac.color('neutral', 700), tombac.unit(12));
const ContractField = ({
  children,
  label,
  ...stylingProps
}) => /*#__PURE__*/_jsxs(Box, {
  $alignItems: "center",
  $display: "grid",
  $gap: "2u",
  $gridTemplateRows: "18u 18u",
  ...stylingProps,
  "data-sentry-element": "Box",
  "data-sentry-component": "ContractField",
  "data-sentry-source-file": "ContractDetails.tsx",
  children: [/*#__PURE__*/_jsx(Label, {
    as: "strong",
    "data-sentry-element": "Label",
    "data-sentry-source-file": "ContractDetails.tsx",
    "data-sentry-element": "Label",
    children: label
  }), ' ', /*#__PURE__*/_jsx(ContractFieldText, {
    as: "span",
    "data-sentry-element": "ContractFieldText",
    "data-sentry-source-file": "ContractDetails.tsx",
    "data-sentry-element": "ContractFieldText",
    children: children
  })]
});
const ContractOrganization = styled(Text).withConfig({
  displayName: "ContractDetails__ContractOrganization"
})(["color:", ";font-size:", ";font-weight:500;"], tombac.color('neutral', 700), tombac.unit(12));
export const ContractDetails = () => {
  const addApiErrorToast = useApiErrorToast();
  const {
    resolveContractOverlaps
  } = useAppApiClient();
  const formatDate = useFormatUserDate();
  const history = useHistory();
  const location = useLocation();
  const {
    addToast
  } = useToasts();
  const meQuery = useMeQuery();
  const role = meQuery.data?.me?.role;
  const params = useParams();
  const contractId = Number(params.contractId);
  const idIsValid = !Number.isNaN(contractId);
  const contractQuery = useContractQuery({
    contractId
  }, {
    enabled: idIsValid,
    staleTime: 0
  });
  const [isContractEditOpen, setIsContractEditOpen] = useState(false);
  const [isCloneContractOpen, setIsCloneContractOpen] = useState(false);
  const [isUsersEditOpen, setIsUsersEditOpen] = useState(false);
  const [isContractExportModalOpen, setIsContractExportModalOpen] = useState(false);
  const [clonedContract, setClonedContract] = useState(null);
  const [cloneOverlaps, setCloneOverlaps] = useState([]);
  const [isRefreshingOverlaps, setIsRefreshingOverlaps] = useState(false);
  const handleCloneContractFinish = addedContract => {
    history.push(`/contracts/${addedContract.id}`, {
      showNewBadge: true
    });
    addToast('Contract has been cloned successfully!', 'success', {
      content: 'Now you can manage contract details, users and limits.'
    });
  };
  const handleCloseOverlapsModal = () => {
    handleCloneContractFinish(clonedContract);
    setCloneOverlaps([]);
    setClonedContract(null);
  };
  const handleOverlapsRefresh = async () => {
    try {
      setIsRefreshingOverlaps(true);
      const overlapsResponse = await resolveContractOverlaps({
        contract: {
          ...clonedContract,
          users: contractQuery.data.users.map(({
            email
          }) => ({
            email
          }))
        }
      });
      if (!overlapsResponse.ok) {
        throw overlapsResponse;
      }
      const overlaps = await overlapsResponse.json();
      setCloneOverlaps([...overlaps]);
      setIsRefreshingOverlaps(false);
    } catch (e) {
      addApiErrorToast(e);
      setIsRefreshingOverlaps(false);
    }
  };
  useEffect(() => {
    if (clonedContract && cloneOverlaps.length > 0) {
      window.addEventListener('focus', handleOverlapsRefresh);
    }
    return () => {
      window.removeEventListener('focus', handleOverlapsRefresh);
    };
  }, [clonedContract, cloneOverlaps]);
  if (!idIsValid || contractQuery.isSuccess && contractQuery.data === null) {
    return /*#__PURE__*/_jsx(NotFound, {});
  }
  if (contractQuery.isError) {
    return /*#__PURE__*/_jsx(Alert, {
      $m: "3sp",
      title: "Could not load contract details",
      variant: "danger",
      children: "Please try again later or contact MOVE support."
    });
  }
  if (contractQuery.isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const contract = contractQuery.data;
  return /*#__PURE__*/_jsxs(Box, {
    $backgroundColor: "--neutral-300",
    $flex: "auto",
    "data-sentry-element": "Box",
    "data-sentry-component": "ContractDetails",
    "data-sentry-source-file": "ContractDetails.tsx",
    children: [/*#__PURE__*/_jsxs(PageHeader, {
      $pt: "3sp",
      "data-sentry-element": "PageHeader",
      "data-sentry-source-file": "ContractDetails.tsx",
      "data-sentry-element": "PageHeader",
      children: [/*#__PURE__*/_jsx(Button, {
        $mb: "2sp",
        $ml: "-1sp",
        as: Link,
        prepend: /*#__PURE__*/_jsx(BackIcon, {}),
        size: "xs",
        to: "/contracts",
        variant: "flat",
        "data-sentry-element": "Button",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "Button",
        children: "Back"
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $justifyContent: "space-between",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(Heading, {
          as: "h1",
          level: 3,
          "data-sentry-element": "Heading",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "Heading",
          children: /*#__PURE__*/_jsxs(Box, {
            $alignItems: "center",
            $display: "flex",
            "data-sentry-element": "Box",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "Box",
            children: [/*#__PURE__*/_jsx("span", {
              children: getContractDisplayName(contract)
            }), location.state?.showNewBadge && /*#__PURE__*/_jsx(Badge, {
              $ml: "2sp",
              children: "New"
            }), /*#__PURE__*/_jsxs(RequireAuth, {
              role: [UserRole.Admin, UserRole.Distributor],
              "data-sentry-element": "RequireAuth",
              "data-sentry-source-file": "ContractDetails.tsx",
              "data-sentry-element": "RequireAuth",
              children: [/*#__PURE__*/_jsx(Tooltip, {
                content: "Edit contract",
                placement: "top",
                size: "s",
                "data-sentry-element": "Tooltip",
                "data-sentry-source-file": "ContractDetails.tsx",
                "data-sentry-element": "Tooltip",
                children: ({
                  close,
                  open,
                  ref
                }) => /*#__PURE__*/_jsx(Button, {
                  $ml: "2sp",
                  onClick: () => setIsContractEditOpen(true),
                  onMouseEnter: open,
                  onMouseLeave: close,
                  ref: ref,
                  shape: "circle",
                  size: "s",
                  variant: "flat",
                  children: /*#__PURE__*/_jsx(EditIcon, {
                    size: "sm"
                  })
                })
              }), /*#__PURE__*/_jsx(Tooltip, {
                content: "Clone contract",
                placement: "top",
                size: "s",
                "data-sentry-element": "Tooltip",
                "data-sentry-source-file": "ContractDetails.tsx",
                "data-sentry-element": "Tooltip",
                children: ({
                  close,
                  open,
                  ref
                }) => /*#__PURE__*/_jsx(Button, {
                  onClick: () => setIsCloneContractOpen(true),
                  onMouseEnter: open,
                  onMouseLeave: close,
                  ref: ref,
                  shape: "circle",
                  size: "s",
                  variant: "flat",
                  children: /*#__PURE__*/_jsx(CopyIcon, {})
                })
              })]
            })]
          })
        }), (role === UserRole.Admin ||
        // role === UserRole.Distributor || hidden until tested enough
        role === UserRole.Support) && /*#__PURE__*/_jsxs(Box, {
          $display: "flex",
          $alignItems: "center",
          children: [/*#__PURE__*/_jsx(Button, {
            onClick: () => setIsContractExportModalOpen(true),
            prepend: /*#__PURE__*/_jsx(ExportIcon, {}),
            variant: "flat",
            children: "Generate report"
          }), /*#__PURE__*/_jsx(ContractReportModal, {
            isModalOpen: isContractExportModalOpen,
            closeModal: () => setIsContractExportModalOpen(false),
            contract: contract,
            role: role
          })]
        })]
      }), /*#__PURE__*/_jsxs(ContractOrganization, {
        $mt: "0.5sp",
        "data-sentry-element": "ContractOrganization",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "ContractOrganization",
        children: [contract.organization.name, contract.organization.sapId && role !== UserRole.Distributor && /*#__PURE__*/_jsxs(_Fragment, {
          children: [' ', /*#__PURE__*/_jsx(Box, {
            $m: "0 1sp",
            as: "span",
            children: "|"
          }), ' ', contract.organization.sapId]
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        $display: "grid",
        $gap: "3sp",
        $gridTemplateColumns: "repeat(5, 150u )",
        $mb: "4sp",
        $mt: "3sp",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(ContractField, {
          label: "Contract type",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: constantToDisplayName(contract.type)
        }), /*#__PURE__*/_jsxs(ContractField, {
          label: "Status",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: [/*#__PURE__*/_jsx(ContractStatusDot, {
            $display: "inline-block",
            $mr: "1sp",
            status: contract.status,
            "data-sentry-element": "ContractStatusDot",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "ContractStatusDot"
          }), constantToDisplayName(contract.status)]
        }), contract.accessType && /*#__PURE__*/_jsxs(ContractField, {
          label: "Access",
          children: [/*#__PURE__*/_jsx(Box, {
            $display: "inline-block",
            $mr: "1sp",
            as: "span",
            children: /*#__PURE__*/_jsx(ContractAccessTypeIcon, {
              accessType: contract.accessType,
              fixedWidth: false
            })
          }), constantToDisplayName(contract.accessType)]
        }), /*#__PURE__*/_jsx(ContractField, {
          $gridRow: "2",
          label: "Start date",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: formatDate(contract.startDate)
        }), /*#__PURE__*/_jsx(ContractField, {
          $gridRow: "2",
          label: "End date",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: formatDate(contract.endDate)
        }), /*#__PURE__*/_jsx(ContractField, {
          $gridRow: "2",
          label: "Term",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: /*#__PURE__*/_jsx(ContractTerm, {
            contract: contract,
            "data-sentry-element": "ContractTerm",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "ContractTerm"
          })
        }), /*#__PURE__*/_jsx(ContractField, {
          $gridRow: "2",
          label: "Expires",
          "data-sentry-element": "ContractField",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "ContractField",
          children: /*#__PURE__*/_jsx(ContractExpires, {
            contract: contract,
            "data-sentry-element": "ContractExpires",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "ContractExpires"
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(Box, {
      $alignItems: "start",
      $boxSizing: "content-box",
      $display: "grid",
      $gap: "4sp 10sp",
      $gridTemplateAreas: "'products users' 'products notes'",
      $gridTemplateColumns: "1fr 1fr",
      $margin: "0 auto",
      $maxWidth: "1440u",
      $padding: "3sp",
      $width: "calc(100% - 6sp )",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ContractDetails.tsx",
      "data-sentry-element": "Box",
      children: [/*#__PURE__*/_jsx(PagePanelContainer, {
        $gridArea: "products",
        "data-sentry-element": "PagePanelContainer",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "PagePanelContainer",
        children: contract.id && /*#__PURE__*/_jsx(ContractProductsContainer, {
          contract: contract
        })
      }), /*#__PURE__*/_jsxs(PagePanelContainer, {
        $gridArea: "users",
        "data-sentry-element": "PagePanelContainer",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "PagePanelContainer",
        children: [/*#__PURE__*/_jsxs(Box, {
          $alignItems: "center",
          $display: "flex",
          $mb: "2sp",
          "data-sentry-element": "Box",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "Box",
          children: [/*#__PURE__*/_jsxs(Heading, {
            as: "h2",
            level: 5,
            "data-sentry-element": "Heading",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "Heading",
            children: ["Users (", contract.users.length, ")"]
          }), /*#__PURE__*/_jsx(RequireAuth, {
            role: [UserRole.Admin, UserRole.Distributor],
            "data-sentry-element": "RequireAuth",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "RequireAuth",
            children: /*#__PURE__*/_jsx(Button, {
              $ml: "auto",
              onClick: () => setIsUsersEditOpen(true),
              prepend: /*#__PURE__*/_jsx(EditIcon, {}),
              size: "xs",
              variant: "flat",
              "data-sentry-element": "Button",
              "data-sentry-source-file": "ContractDetails.tsx",
              "data-sentry-element": "Button",
              children: "Manage users"
            })
          })]
        }), /*#__PURE__*/_jsx(PagePanel, {
          $margin: "0",
          breakpoint: 0,
          "data-sentry-element": "PagePanel",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "PagePanel",
          children: /*#__PURE__*/_jsx(ContractUserList, {
            $m: contract.users.length ? '1sp -3sp 0' : '0 -3sp',
            users: contract.users,
            "data-sentry-element": "ContractUserList",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "ContractUserList"
          })
        })]
      }), /*#__PURE__*/_jsxs(PagePanelContainer, {
        $gridArea: "notes",
        $gridRow: "span 2",
        "data-sentry-element": "PagePanelContainer",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "PagePanelContainer",
        children: [/*#__PURE__*/_jsx(Box, {
          $alignItems: "center",
          $display: "flex",
          $mb: "2sp",
          "data-sentry-element": "Box",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "Box",
          children: /*#__PURE__*/_jsx(Heading, {
            as: "h2",
            level: 5,
            "data-sentry-element": "Heading",
            "data-sentry-source-file": "ContractDetails.tsx",
            "data-sentry-element": "Heading",
            children: "Notes"
          })
        }), /*#__PURE__*/_jsx(PagePanel, {
          $gridArea: "notes",
          $margin: "0",
          breakpoint: 0,
          "data-sentry-element": "PagePanel",
          "data-sentry-source-file": "ContractDetails.tsx",
          "data-sentry-element": "PagePanel",
          children: contract.notes ? /*#__PURE__*/_jsx(Text, {
            as: "div",
            fontSize: 12,
            children: contract.notes
          }) : /*#__PURE__*/_jsx(Text, {
            fontSize: 12,
            variant: "secondary",
            children: "No notes"
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(ContractFormModal, {
      contract: contract,
      intent: isCloneContractOpen ? 'clone' : 'default',
      isOpen: isContractEditOpen || isCloneContractOpen,
      onCancel: () => {
        setIsContractEditOpen(false);
        setIsCloneContractOpen(false);
      },
      onSaveSuccess: (addedContract, {
        userOverlaps
      }) => {
        if (isCloneContractOpen) {
          if (userOverlaps.length > 0) {
            setClonedContract(addedContract);
            setCloneOverlaps(userOverlaps);
          } else {
            handleCloneContractFinish(addedContract);
          }
        }
        setIsContractEditOpen(false);
        setIsCloneContractOpen(false);
        contractQuery.refetch();
      },
      "data-sentry-element": "ContractFormModal",
      "data-sentry-source-file": "ContractDetails.tsx",
      "data-sentry-element": "ContractFormModal"
    }), clonedContract && /*#__PURE__*/_jsx(ContractAssignOverlapsModal, {
      isRefreshing: isRefreshingOverlaps,
      isOpen: cloneOverlaps.length > 0,
      onAssign: handleCloseOverlapsModal,
      onRequestClose: handleCloseOverlapsModal,
      onRequestRefresh: handleOverlapsRefresh,
      overlaps: cloneOverlaps,
      targetContractId: clonedContract.id
    }), /*#__PURE__*/_jsx(Modal, {
      className: "AssignContractUsersModal",
      isOpen: isUsersEditOpen,
      "data-sentry-element": "Modal",
      "data-sentry-source-file": "ContractDetails.tsx",
      "data-sentry-element": "Modal",
      children: /*#__PURE__*/_jsx(AssignContractUsers, {
        contract: contract,
        onAssignSuccess: () => setIsUsersEditOpen(false),
        onCancel: () => setIsUsersEditOpen(false),
        userRole: role,
        "data-sentry-element": "AssignContractUsers",
        "data-sentry-source-file": "ContractDetails.tsx",
        "data-sentry-element": "AssignContractUsers"
      })
    })]
  });
};
const Badge = styled.span.withConfig({
  displayName: "ContractDetails__Badge"
})(["align-items:center;background-color:", ";border-radius:", ";color:", ";display:flex;font-size:", ";font-weight:700;height:", ";padding:", ";text-transform:uppercase;", ""], tombac.color('accent', 200), tombac.unit(5), tombac.color('accent'), tombac.unit(14), tombac.space(4), tombac.space(0, 1), propStyling);
const PagePanelContainer = styled.div.withConfig({
  displayName: "ContractDetails__PagePanelContainer"
})(["", ""], propStyling);